import { FieldDefinition, FieldOption, FieldType } from './dynamic-form.types.ts';

export const getDefaultForType = (type: FieldType): unknown => {
  switch (type) {
    case 'number':
    case 'range':
      return 0;
    case 'checkbox':
      return false;
    case 'multiselect':
      return [];
    case 'text':
    case 'email':
    case 'password':
    case 'textarea':
    case 'select':
    case 'radio':
    case 'date':
    case 'time':
    case 'datetime':
    case 'color':
    case 'file':
    default:
      return '';
  }
};

export const createInitialValues = (
  inputs: Record<string, FieldDefinition>
): Record<string, unknown> => {
  return Object.entries(inputs).reduce(
    (acc, [key, field]) => {
      acc[key] = field.default ?? getDefaultForType(field.type);
      return acc;
    },
    {} as Record<string, unknown>
  );
};

export const isFieldVisible = (
  field: FieldDefinition,
  formValues: Record<string, unknown>
): boolean => {
  if (!field.dependsOn) return true;
  return formValues[field.dependsOn.field] === field.dependsOn.value;
};

export const normalizeOptions = (
  options: Array<string | FieldOption> | undefined
): FieldOption[] => {
  if (!options) return [];

  return options.map((option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    return option;
  });
};

export const getValidationRules = (field: FieldDefinition) => {
  const rules: Record<string, unknown> = {};

  if (field.required) {
    rules.required = 'This field is required';
  }

  if (field.type === 'number' || field.type === 'range') {
    if (field.min !== undefined) {
      rules.min = { value: field.min, message: `Minimum value is ${field.min}` };
    }
    if (field.max !== undefined) {
      rules.max = { value: field.max, message: `Maximum value is ${field.max}` };
    }
  }

  if (
    field.type === 'text' ||
    field.type === 'textarea' ||
    field.type === 'email' ||
    field.type === 'password'
  ) {
    if (field.minLength !== undefined) {
      rules.minLength = {
        value: field.minLength,
        message: `Minimum length is ${field.minLength}`,
      };
    }
    if (field.maxLength !== undefined) {
      rules.maxLength = {
        value: field.maxLength,
        message: `Maximum length is ${field.maxLength}`,
      };
    }
  }

  if (field.pattern) {
    rules.pattern = {
      value: new RegExp(field.pattern),
      message: 'Invalid format',
    };
  }

  if (field.type === 'email') {
    rules.pattern = {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    };
  }

  return rules;
};
